<template>
  <main
    role="main"
    class="i-main"
  >
    <section class="main-krpano-wrapper">
      <!--has building, but no panoramas-->
      <div
        v-if="isNoPanoramasFound"
        class="full-center error-wrapper"
      >
        <figure class="error-wrapper-container">
          <img
            :src="noPanoramasImage"
            alt="no panoramas"
          >
          <h3>{{ $t('noPanoramas') }}</h3>
        </figure>
      </div>
      <template v-else>
        <video-panorama
          v-if="currentPanorama.videoAttribute"
          :key="currentPanorama.index"
          :video="currentPanorama.videoAttribute"
          @end="redirectToNextPanorama"
        />
        <krpano
          v-show="!currentPanorama.videoAttribute"
          :is-mouse-on-marker-info="isMouseOnMarkerInfo"
          :is-mouse-on-krpano-marker="isMouseOnKrpanoMarker"
          :mouseenter-marker="mouseenterMarker"
          :mouseleave-marker="mouseleaveMarker"
          @setMarkerPositionX="markerPositionX = $event"
          @setMarkerPositionY="markerPositionY = $event"
          @setIsMouseOnKrpanoMarker="isMouseOnKrpanoMarker = $event"
          @hook:narration="onNarrationSelected"
          @launch-videoplay-fullscreen="isVideoplayFullscreen = true"
          @startup="isHaveStartUp = true"
        />
      </template>
    </section>

    <!--to show marker point-->
    <viewer-markers-hover
      :marker-position-x="markerPositionX"
      :marker-position-y="markerPositionY"
      :is-mouse-on-marker-info="isMouseOnMarkerInfo"
      :is-mouse-on-krpano-marker="isMouseOnKrpanoMarker"
      :mouseenter-marker="mouseenterMarker"
      :mouseleave-marker="mouseleaveMarker"
      @setMouseOnMarkerInfo="isMouseOnMarkerInfo = $event"
    />

    <!--to show marker memo/tag-->
    <viewer-layer
      v-show="!isVrMode && markerInfoData.type !== 'gallery'"
      class="bounce-in"
      :class="{ 'bounce-in-active': isMarkerInfoActive && !isArViewPopupActive }"
      :inner-class-name="markerInfoData.type === 'multitag' ? 'viewer-layer-enlarge no-close-btn': ''"
      :close-event="closeMarkerInfo"
    >
      <template #inner-extra>
        <i-favorite
          v-if="favoritesMarkerTypes.includes(markerInfoData.type)"
          category="marker"
          :item="markerInfoData"
        />
      </template>
      <ul
        v-show="markerInfoData.type === 'memo'"
        class="viewer-layer-tabs"
      >
        <li class="viewer-layer-li full-width">
          <span
            v-if="markerInfoData.nameMulti"
            class="viewer-layer-text"
          >
            {{ markerInfoData.nameMulti[$i18n.locale] || markerInfoData.name }}
          </span>
          <span
            v-else
            class="viewer-layer-text"
          >
            {{ markerInfoData.name }}
          </span>
        </li>
      </ul>
      <div class="viewer-layer-container">
        <marker-info />
      </div>
    </viewer-layer>

    <!--to show marker gallery-->
    <viewer-layer
      v-show="!isVrMode && markerInfoData.type === 'gallery'"
      class="bounce-in"
      :class="{ 'bounce-in-active': isMarkerInfoActive }"
      :close-event="closeMarkerInfo"
      inner-class-name="viewer-layer-enlarge height-auto"
    >
      <template #inner-extra>
        <i-favorite
          v-if="favoritesMarkerTypes.includes(markerInfoData.type)"
          category="marker"
          :item="markerInfoData"
        />
      </template>
      <marker-info />
    </viewer-layer>

    <!--search-->
    <viewer-layer
      v-if="isMarkerFinderEnable"
      v-show="shouldViewerLayerShow"
      class="bounce-in"
      :class="{ 'bounce-in-active': isSearchActive }"
      :close-event="closeSearchs"
      inner-class-name="viewer-layer-second-inner fullscreen"
    >
      <div class="search-wrap h-100">
        <template v-if="isSearchInit">
          <div class="search-block">
            <i-category-select
              class="category-search"
            />
            <i-marker-search class="marker-search" />
          </div>
          <!-- isSearchActive: 讓 layer 關閉後重新開啓時滾動位置能回到原點 -->
          <search-list
            :is-search-result-null-for-taiwantrade="isSearchResultNullForTaiwantrade"
            @gotoTarget="gotoTarget"
            @close="closeSearchs"
          />
        </template>
        <template v-else>
          <div class="search-loading">
            <img src="@/assets/images/loading.svg">
          </div>
        </template>
      </div>
    </viewer-layer>

    <!--comments-->
    <viewer-layer
      v-if="showComment"
      v-show="shouldViewerLayerShow"
      class="bounce-in"
      :class="{ 'bounce-in-active': isCommentsActive }"
      :close-event="closeComments"
      inner-class-name="viewer-layer-second-inner"
    >
      <ul class="viewer-layer-tabs">
        <li class="viewer-layer-li viewer-layer-li-active">
          <span class="viewer-layer-text">{{ $t('comment') }}</span>
        </li>
      </ul>
      <div class="viewer-layer-container">
        <comments-list @setCommentsEl="setCommentsEl" />
        <comments-input @set-comments-el-scroll-to-top="setCommentsElScrollToTop" />
      </div>
    </viewer-layer>

    <!--information-->
    <viewer-layer
      v-show="shouldViewerLayerShow"
      class="bounce-in"
      :class="{ 'bounce-in-active': isInformationActive }"
      :close-event="closeInformation"
    >
      <ul class="viewer-layer-tabs">
        <li class="viewer-layer-li viewer-layer-li-active">
          <span class="viewer-layer-text">{{ $t('propertyInfo') }}</span>
        </li>
      </ul>
      <div class="viewer-layer-container">
        <information
          :name="property.name"
          :description="property.description"
        />
      </div>
    </viewer-layer>

    <!--google map-->
    <viewer-layer
      v-if="shouldGoogleMapActive"
      v-show="shouldViewerLayerShow"
      class="bounce-in"
      :class="{ 'bounce-in-active': isLocationActive }"
      :close-event="closeLocation"
    >
      <ul class="viewer-layer-tabs">
        <li class="viewer-layer-li viewer-layer-li-active">
          <span class="viewer-layer-text">{{ $t('location') }}</span>
        </li>
      </ul>
      <div class="viewer-layer-container">
        <i-map
          v-show="isLocationActive"
          :lat="currentBuilding.geoLatitude"
          :lng="currentBuilding.geoLongitude"
        />
      </div>
    </viewer-layer>

    <!--share-->
    <viewer-layer
      v-show="shouldViewerLayerShow && isShareReady"
      class="bounce-in"
      :class="{ 'bounce-in-active': isShareActive }"
      :close-event="closeShare"
    >
      <ul class="viewer-layer-tabs">
        <li class="viewer-layer-li viewer-layer-li-active">
          <span class="viewer-layer-text">{{ $t('share') }}</span>
        </li>
      </ul>
      <div class="viewer-layer-container">
        <div class="share-container">
          <qr-code
            v-if="shareUrl.qrcode"
            :url="shareUrl.qrcode"
          />
          <share-list />
          <button
            v-if="isIframe"
            class="btn-close"
            @click="closeShare"
            v-text="$t('close')"
          />
        </div>
      </div>
    </viewer-layer>

    <narrator
      v-show="!shouldPilotRecorderActive && !shouldPilotPlayerActive"
      :should-panoramas-list-show="shouldPanoramasListShow"
    />
    <!--menu and home btn-->
    <div class="main-float-btns">
      <div
        v-show="currentBuilding.homeButtonEnable"
        class="home-btn"
        :style="{
          pointerEvents: isMobileMenuActive ? 'none' : 'auto'
        }"
        @click="homeIconClick"
      >
        <i class="isif isif-home home-icon" />
      </div>
      <div
        v-show="!isHideMenu"
        class="expand-btn"
        :style="{
          pointerEvents: isMobileMenuActive ? 'none' : 'auto'
        }"
        @click="expandMenuIconClick"
      >
        <i class="isif isif-expand expand-icon" />
      </div>
    </div>
    <!--bottom buildings and panoramas-->
    <section
      v-show="shouldViewerLayerShow"
      class="main-footer"
      :class="{ 'main-footer-active': shouldPanoramasListShow }"
    >
      <div class="main-footer-tool">
        <div class="main-footer-tool-1">
          <button
            type="button"
            class="main-viewer-list-collapse"
            @click="togglePanoramasList"
          >
            <i
              class="isif"
              :class="shouldPanoramasListShow ? 'isif-arrow-down' : 'isif-arrow-up'"
            />
          </button>
        </div>
      </div>
      <viewer-list class="main-viewer-list" />
    </section>

    <!-- floorplan -->
    <section
      v-if="!currentBuilding.floorplanSize && floorplan && floorplan !== 'none'"
      v-show="shouldViewerLayerShow"
      class="floorplan-wrapper"
      :class="{
        'floorplan-wrapper-active': shouldFloorplanShow,
        'floorplan-wrapper-has-buildings': property.objectId
      }"
    >
      <floorplan
        v-show="isFloorplanReady"
        class="floorplan-self"
        :floorplan-container-width="floorplanContainerWidth"
        :floorplan-container-height="floorplanContainerHeight"
        :is-resizable="true"
        :is-draggable="true"
      />
      <span
        class="floorplan-wrapper-collapse"
        @click="toggleFloorplan"
      >
        <icon
          :class="{
            'icon-collapse-right': !isFloorplanActive,
            'icon-collapse-left': isFloorplanActive
          }"
        />
      </span>
    </section>

    <!-- enlarge floorplan -->
    <section
      v-if="!!currentBuilding.floorplanSize && floorplan && floorplan !== 'none'"
      class="enlarge-floorplan-wrapper"
      :class="{ 'enlarge-floorplan-wrapper-active': shouldFloorplanShow}"
    >
      <EnlargeFloorplan />
    </section>
    <!--ios enter vr mode but is portrait-->
    <instructions />

    <!--marker popup (use old)-->
    <popup
      v-if="isPopupActive"
      :url="popupUrl"
      :type="popupSizeConfig.type"
      :full-height="popupSizeConfig.fullHeight"
      :width-percent="popupSizeConfig.widthPercent"
      :transparent="popupSizeConfig.transparent"
      :marker="popupMarker"
      @closePopup="closePopup"
    />

    <!-- arview popup -->
    <popup-ar-view
      v-if="isArViewPopupActive"
      @closeMarkerInfo="closeMarkerInfo"
    />

    <VisitorWelcome
      class="bounce-in"
      :class="{ 'bounce-in-active': isVisitorInfoActive }"
    />

    <!-- Music Player -->
    <MusicPlayer
      v-show="shouldMusicPlayerShow"
      ref="MusicPlayer"
      class="i-main-music-player"
      @pause="onMusicPlayerPause"
      @playing="onMusicPlayerPlaying"
      @stop="setMusicPlayerActive(false)"
      @ended="setMusicPlayerActive(false)"
    />

    <!-- Pilot Recorder -->
    <pilot-recorder
      v-if="shouldPilotRecorderActive"
    />

    <!-- Pilot Player -->
    <pilot-player />

    <!-- VR Lounge -->
    <vr-lounge />

    <fullscreen-video
      v-if="currentMarker && currentMarker.type === 'videoplay'"
      style="z-index: 5"
      :url="currentMarker.actionLink"
      :is-fullscreen="isVideoplayFullscreen"
      @change="('isFullscreen' in $event) && exitFullscreenVideo()"
    />

    <!-- Favorites -->
    <favorites-dialog
      :layer-visible="shouldViewerLayerShow"
      :goto-target="gotoTarget"
    />

    <!-- Pilot mask -->
    <pilot-mask />
  </main>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import CommentsInput from '@/components/common/ViewerLayer/Comments/CommentsInput'
import CommentsList from '@/components/common/ViewerLayer/Comments/CommentsList'
import EnlargeFloorplan from '@/components/common/Floorplan/EnlargeFloorplan'
import FavoritesDialog from '@/components/common/Favorites/Dialog'
import Floorplan from '@/components/common/Floorplan/index'
import FullscreenVideo from '@/components/FullscreenVideo'
import ICategorySelect from '@/components/ICategorySelect'
import Icon from '@/components/Icon/index'
import IFavorite from '@/components/IFavorite'
import IMap from '@/components/IMap'
import IMarkerSearch from '@/components/IMarkerSearch'
import Information from '@/components/common/ViewerLayer/Information'
import Instructions from '@/components/common/Instructions/index'
import Krpano from '@/components/common/Krpano/index'
import MarkerInfo from '@/components/common/ViewerLayer/MarkerInfo'
import MusicPlayer from '@/components/MusicPlayer'
import Narrator from '@/components/common/Narrator'
import PilotPlayer from '@/components/common/Pilot/PilotPlayer'
import PilotRecorder from '@/components/common/Pilot/PilotRecorder/index'
import PilotMask from '@/components/common/Pilot/PilotMask'
import Popup from '@/components/Popup/index'
import PopupArView from '@/components/Popup/PopupArView'
import QrCode from '@/components/QrCode'
import SearchList from '@/components/Search/SearchList'
import ShareList from '@/components/common/ShareList/index'
import VideoPanorama from '@/components/VideoPanorama'
import ViewerLayer from '@/components/common/ViewerLayer/index'
import ViewerList from '@/components/common/ViewerList/index'
import ViewerMarkersHover from '@/components/common/ViewerMarkersHover/index'
import VisitorWelcome from '@/components/common/VisitorWelcome/VisitorWelcome'
import VrLounge from '@/components/VrLounge'
import visitorApi from '@/services/api/visitor'
import { isIframe } from '@/api/utils'
import { getMarkerProfile } from '@/api/helpers'
import gaEvents from '@/api/ga-events'
import { DISPLAY_SEARCH_RESULT_QTY, KRPANO_SETTING, COMPANY } from '@/api/constants'
import queryString from '@/api/query-string'
import webStorage from '../../store/webStorage/helper'

export default {
  name: 'IMain',
  components: {
    CommentsInput,
    CommentsList,
    Floorplan,
    EnlargeFloorplan,
    IMap,
    Icon,
    IMarkerSearch,
    ICategorySelect,
    Information,
    Instructions,
    Krpano,
    MarkerInfo,
    MusicPlayer,
    PilotRecorder,
    PilotPlayer,
    PilotMask,
    Popup,
    PopupArView,
    QrCode,
    ShareList,
    ViewerLayer,
    ViewerList,
    ViewerMarkersHover,
    VisitorWelcome,
    Narrator,
    VrLounge,
    FullscreenVideo,
    FavoritesDialog,
    IFavorite,
    VideoPanorama,
    SearchList
  },

  data () {
    return {
      commentsEl: null,
      floorplanContainerHeight: 250,
      floorplanContainerWidth: 250,
      isMapReady: false,
      isMouseOnKrpanoMarker: false,
      isMouseOnMarkerInfo: false,
      isShareReady: false,
      isFloorplanReady: false,
      isVideoplayFullscreen: false,
      markerPositionX: 0,
      markerPositionY: 0,
      noPanoramasImage: require('@/assets/images/trash-can.svg'),
      currentMusicMarker: null,
      DISPLAY_SEARCH_RESULT_QTY,
      isHaveStartUp: false,
      lang: this.$i18n.locale
    }
  },

  computed: {
    ...mapState('favorites', {
      favoritesMarkerTypes: state => state.acceptedMarkerTypes
    }),

    ...mapGetters([
      'currentBuilding',
      'currentPanorama',
      'lastPanorama',
      'currentMarker',
      'customSetting',
      'floorplan',
      'isCommentsActive',
      'isFloorplanActive',
      'krpanoEl',
      'krpanoHooks',
      'krpanoXOffset',
      'isFullscreen',
      'isGyroFromIframe',
      'isInformationActive',
      'isLocationActive',
      'isMarkerInfoActive',
      'isNoPanoramasFound',
      'isPanoramasListActive',
      'isPopupActive',
      'isMobileMenuActive',
      'isShareActive',
      'isVisitorInfoActive',
      'isMusicPlayerActive',
      'isBuildingInfoActive',
      'isGuideWindowActive',
      'isOwnerInfoActive',
      'isVrMode',
      'markerInfoData',
      'panoramas',
      'popupSizeConfig',
      'popupUrl',
      'popupMarker',
      'property',
      'shareUrl',
      'showComment',
      'isSearchInit',
      'isSearchActive',
      'isMarkerFinderEnable',
      'isPropertyActive',
      'markers',
      'user',
      'searchKeywords',
      'headquarter',
      'searchLiveTourData',
      'combineSearchResult',
      'combineRelatedResult',
      'visitorHasPass',
      'isPilotPlayerActive',
      'isScreenReady'
    ]),
    ...mapGetters({
      isFavoritesEnabled: 'favorites/enabled',
      isLogin: 'auth/isLogin',
      authUser: 'auth/user'
    }),
    ...mapGetters('arview', [
      'isArViewPopupActive'
    ]),

    isIframe,

    isHidePanoramaNavigation () {
      return this.$route.query.hidePanoramaNavigation === 'true'
    },

    shouldViewerLayerShow () {
      return !(this.isVrMode || this.isFullscreen || this.isHidePanoramaNavigation)
    },

    shouldPanoramasListAndFloorplanShow () {
      return !this.isShareActive &&
        !this.isMarkerInfoActive &&
        !this.isInformationActive &&
        !this.isCommentsActive &&
        !this.isLocationActive
    },

    shouldPanoramasListShow () {
      if (this.isPanoramasListActive) {
        return this.shouldPanoramasListAndFloorplanShow
      }
      return false
    },

    shouldFloorplanShow () {
      if (this.isFloorplanActive) {
        return !this.isFullscreen && this.shouldPanoramasListAndFloorplanShow
      }
      return false
    },

    shouldGoogleMapActive () {
      return process.env.USE_GOOGLE_MAP &&
        this.customSetting.googleMap &&
        this.currentBuilding.mapEnable &&
        this.isMapReady
    },

    shouldTourRingBtnShow () {
      return this.currentBuilding.tourRingBtn
    },

    shouldMusicPlayerShow () {
      return this.isMusicPlayerActive &&
        !this.isShareActive &&
        !this.isMarkerInfoActive &&
        !this.isInformationActive &&
        !this.isCommentsActive &&
        !this.isLocationActive &&
        !this.isBuildingInfoActive &&
        !this.isOwnerInfoActive &&
        !this.isGuideWindowActive
    },

    MusicPlayerRef () {
      return this.$refs.MusicPlayer
    },

    shouldPilotRecorderActive () {
      return !_.isNil(this.$route.query.recordPilot)
    },

    shouldPilotPlayerActive () {
      return this.isPilotPlayerActive
    },

    displaySearchResult () {
      return this.searchResult.slice(0, this.DISPLAY_SEARCH_RESULT_QTY)
    },

    isSearchResultNullForTaiwantrade () {
      return this.user.headquarter.title === COMPANY.TAIWANTRADE && !this.searchKeywords
    },
    isHideMenu () {
      return this.$route.query.hideMenu === 'true'
    }
  },

  watch: {
    shareUrl: {
      handler () {
        this.initShare()
      },
      deep: true
    },

    currentBuilding: {
      handler (newValue, oldValue) {
        this.initMap()
      },
      deep: true
    },

    currentPanorama () {
      this.stopMusicPlayer()
    },

    shouldMusicPlayerShow (newValue, oldValue) {
      if (!newValue && oldValue) this.stopMusicPlayer()
    },

    isLogin: {
      immediate: true,
      handler (val) {
        if (val) {
          if (this.isFavoritesEnabled) {
            this.initFavorite()
          }
        }
      }
    },

    visitorHasPass: {
      handler (val) {
        if (val) {
          if (this.currentBuilding.requireVisitorData) {
            this.startViewSession()
          }
        }
      }
    },

    isScreenReady (newValue) {
      if (newValue && queryString.showFloatMenu && window.innerWidth < 1024) {
        setTimeout(() => {
          this.expandMenuIconClick()
        }, 400)
      }
    }
  },

  async beforeMount () {
    try {
      const sessionToken = this.$route.query.sessionToken || webStorage.getItem(`session-token-${process.env.NODE_ENV}`)
      if (sessionToken && sessionToken !== 'undefined') {
        const result = await this.autoLoginBySession(sessionToken)
        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            sessionToken: undefined
          }
        })
        this.$mixpanel.setPeopleInfo({
          name: result.name || '',
          email: String(result.email || result.username).toLowerCase(),
          phone: result.phone || ''
        })
        this.$mixpanel.start()
        webStorage.setItem('istaging-whitelist-username', String(result.username).toLowerCase())

        /* Detail visitor tracking - start session on auto login */
        this.startDetailViewSession(this.currentBuilding.objectId)
      } else {
        this.setSessionLoginLoading(false)
      }
    } catch (error) {
      console.error(error)
    }
    this.setUpJoorService()
  },

  mounted () {
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler, false)
    document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler, false)
    document.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler, false)
    window.addEventListener('resize', this.resizeHandler)

    this.isFloorplanReady = true
    this.initShare()
    this.initMap()
    this.resizeHandler()

    if (sessionStorage.getItem('marker')) {
      this.directToTarget(JSON.parse(sessionStorage.getItem('marker')))
    }

    this.$mixpanel.start()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.resizeHandler)
  },

  methods: {
    ...mapActions([
      'closeMobileMenu',
      'closeSearchs',
      'closeComments',
      'closeInformation',
      'closeLocation',
      'closeMarkerInfo',
      'closePopup',
      'closeShare',
      'closeVisitor',
      'exitFullscreen',
      'exitVrMode',
      'setAudioVolume',
      'stopAutoRotate',
      'setHoveredPanorama',
      'setMarker',
      'setSearchKeywords',
      'selectPanorama',
      'selectBuilding',
      'toggleFloorplan',
      'togglePanoramasList',
      'startViewSession',
      'startRecordVisitorViewSessionSec',
      'showMobileMenu',
      'changeIsClickAvatarState',
      'showPilotTooltip',
      'setPilotTooltipOffset'
    ]),

    ...mapActions('favorites', {
      showFavoritesDialog: 'showDialog',
      initFavorite: 'init'
    }),

    ...mapActions('auth', [
      'autoLoginBySession',
      'setSessionLoginLoading'
    ]),

    ...mapActions('joor', [
      'initJoorService'
    ]),

    ...mapActions('visitorHistory', {
      startDetailViewSession: 'startViewSession'
    }),
    getMarkerProfile,
    homeIconClick () {
      if (this.currentPanorama.objectId !== this.panoramas[0].objectId) {
        this.$mixpanel.track('Change Panorama', {
          buildingId: this.panoramas[0].buildingId,
          objectId: this.panoramas[0].objectId
        })

        this.selectPanorama(this.panoramas[0])
        this.closeInformation()
        this.closeShare()
        this.closeMarkerInfo()
        this.closeLocation()
      }
    },
    expandMenuIconClick () {
      if (!this.isMobileMenuActive) {
        this.changeIsClickAvatarState(false)
        this.showMobileMenu()

        this.$nextTick(() => {
          if (!this.alreadyShownPilotTooltip) {
            const pilotDom = document.getElementById('pilot-mobile-id')
            if (pilotDom !== null) {
              const { height, top, right } = pilotDom.getBoundingClientRect()
              this.setPilotTooltipOffset({ x: right + 375, y: top + (height / 2) })
              this.showPilotTooltip()
            }
          }
        })
      }
    },
    fullscreenChangeHandler () {
      if (document.webkitIsFullScreen === false) {
        this.doExitFullscreen()
      } else if (document.mozFullScreen === false) {
        this.doExitFullscreen()
      } else if (document.msFullscreenElement === false) {
        this.doExitFullscreen()
      }
    },

    redirectToNextPanorama () {
      const index = this.currentPanorama.index
      const length = this.panoramas.length
      if (length === 1) { // If it's the only one, do nothing
        return
      }
      let nextIndex = 0
      if (index + 1 < length) {
        nextIndex = index + 1
      }
      this.selectPanorama(this.panoramas[nextIndex])
    },

    async gotoTarget (target, isMarker = true) {
      this.closeInformation()
      this.closeSearchs()
      this.closePopup()
      this.closeShare()
      this.closeMarkerInfo()
      this.closeLocation()
      this.setMusicPlayerActive(false)
      this.showFavoritesDialog(false)

      if (isMarker) {
        this.gotoMarker(target)
      } else {
        this.gotoPano(target)
      }
    },

    async gotoMarker (target, noAnimation = false) {
      const isMarkerInCurrentBuilding = this.currentBuilding.objectId === target.buildingId
      const isMarkerInCurrentPanorama = this.currentPanorama.objectId === target.panoramaId
      if (!isMarkerInCurrentBuilding) {
        if (this.checkIsNeedNavigate(target)) {
          sessionStorage.setItem('marker', JSON.stringify(target))
          window.open(`${location.origin}/${target.buildingId}`, '_blank')
          sessionStorage.removeItem('marker')
          return
        }
        const param = {
          buildingId: target.buildingId
        }
        if (target.panoramaIndex) {
          param.index = target.panoramaIndex
        } else {
          param.panorama = target.panoramaId
        }
        this.selectBuilding(param)
        this.krpanoHooks.one('startup')
      } else if (!isMarkerInCurrentPanorama) {
        // target marker must be in the currentBuilding
        const panorama = this.panoramas.find(panorama => panorama.objectId === target.panoramaId)
        this.selectPanorama(panorama)
        this.krpanoHooks.one('change_scene')
      }
      // !!!!! 這邊不適合用this.currentPanorama 或 this.markers
      // 是因為樓上 change_scene 不代表 currentPanoram 或 markers更新
      // 因為 markers 可能非同步 這個不好解 QQQQ
      // 但還是必須等待change_scene完才 call pan_to喔
      const panorama = this.panoramas.find(panorama => panorama.objectId === target.panoramaId)
      const marker = panorama.markers.find(mkr => mkr.objectId === target.markerId)
      const markerIndex = panorama.markers.indexOf(marker)
      this.krpanoHooks.set('pan', () => {
        if (['tag', 'memo', 'gallery', 'multitag'].includes(marker.type)) {
          window.krpanoObj.call(`set_marker_info(${markerIndex});`)
        } else if (['narration'].includes(marker.type)) {
          window.krpanoObj.call(`set_marker_narration(${markerIndex});`)
        } else if (['popup', 'arMakerTag', 'pdf'].includes(marker.type)) {
          window.krpanoObj.call(`handle_show_popup(${markerIndex});`)
        }
      })
      this.stopAutoRotate()
      const speed = noAnimation ? 0 : KRPANO_SETTING.speed.instant
      window.krpanoObj.call(`pan_to(${marker.position.ath}, ${speed}, 'pan')`)
    },

    gotoPano (target) {
      const isSameBuilding = this.currentBuilding.objectId === target.buildingId
      const isSamePanorama = this.currentPanorama.objectId === target.panoramaId
      if (!isSameBuilding) {
        this.selectBuilding({
          buildingId: target.buildingId,
          index: target.panoramaIndex
        })
      } else if (!isSamePanorama) {
        this.selectPanorama(target.panoramaIndex)
      }
    },

    mouseenterMarker (marker) {
      if (this.isVideoplayFullscreen || this.isFloorplanActive) return
      this.setMarker(marker)
      if (marker.type === 'point' && marker.nextPanoramaId) {
        const foundPanorama = this.panoramas.find(panorama =>
          panorama.objectId === marker.nextPanoramaId
        ) || {}
        this.setHoveredPanorama(foundPanorama)
      }
    },

    mouseleaveMarker () {
      if (this.isVideoplayFullscreen) return
      this.setMarker({})
      this.setHoveredPanorama({})
    },

    doExitFullscreen () {
      if (this.isFullscreen) {
        this.exitFullscreen()
      } else if (this.isVrMode) {
        this.exitVrMode()
      }
    },

    resizeHandler () {
      // 768 = CSS assets/css/variables.styl $response value
      if (window.innerWidth >= 768) {
        this.floorplanContainerWidth = 300
        this.floorplanContainerHeight = 300
      } else {
        this.floorplanContainerWidth = 250
        this.floorplanContainerHeight = 250
      }
    },
    // 如果放在 vuex state，CommentsList.vue 會一直觸發 mounted
    setCommentsEl (commentsEl = null) {
      this.commentsEl = commentsEl
    },

    setCommentsElScrollToTop () {
      if (this.commentsEl) {
        this.commentsEl.scrollTop = 0
      }
    },

    initShare () {
      this.isShareReady = false
      this.$nextTick(() => {
        this.isShareReady = true
      })
    },

    initMap () {
      this.isMapReady = false
      this.$nextTick(() => {
        this.isMapReady = true
      })
    },

    setMusicPlayerActive (bool) {
      this.$store.dispatch('setMusicPlayerActive', bool)
    },

    stopMusicPlayer () {
      this.MusicPlayerRef.stop()
      this.currentMusicMarker = null
    },

    onMusicPlayerPlaying () {
      this.setAudioVolume(0.2)
    },

    onMusicPlayerPause () {
      this.setAudioVolume(1.0)
    },

    onNarrationSelected (marker) {
      const { currentMusicMarker, MusicPlayerRef } = this

      // show music player
      this.setMusicPlayerActive(true)

      // check wether same marker
      const cmid = _.get(currentMusicMarker, 'objectId')
      const mid = _.get(marker, 'objectId')
      if (cmid === mid) {
        MusicPlayerRef.toggle()
        return
      }

      // set current marker
      this.currentMusicMarker = marker

      // set player soure
      const lang = this.$i18n.locale
      const obj = {
        title: marker.nameMulti[lang] || marker.name,
        source: marker.actionLink,
        category: marker.categoryMulti[lang] || marker.category
      }
      this.MusicPlayerRef.load(obj)

      this.$mixpanel.track('Click Marker', {
        buildingId: marker.buildingId,
        objectId: marker.objectId,
        type: marker.type,
        name: marker.name
      })
      gaEvents.sendEvent('Marker', 'NarrationClicks', marker.objectId)
      visitorApi.addClickCount(marker.objectId)
    },

    showMoreDisplaySearchResult () {
      const top = this.$refs.searchResult.scrollTop
      this.DISPLAY_SEARCH_RESULT_QTY += DISPLAY_SEARCH_RESULT_QTY
      this.$nextTick(() => {
        // 防止載入更多時捲軸自動跑到最下面
        this.$refs.searchResult.scrollTop = top
      })
    },

    exitFullscreenVideo () {
      this.isVideoplayFullscreen = false
      const { objectId, videoplayAttribute } = this.currentMarker
      window.krpanoObj.call(`set(hotspot[marker_${objectId}].muted, ${videoplayAttribute.mute})`)
    },

    checkWhiteListVerify (whiteList = [], username = '') {
      return !!whiteList.includes(username)
    },

    async setUpJoorService () {
      if (window === window.parent) return
      try {
        const searchParams = new URLSearchParams(document.location.search.substring(1))
        const code = searchParams.get('code')
        const accountIds = searchParams.get('account_ids') || ''
        const accountId = accountIds.split(',')[0] || ''
        const retailerId = accountIds.split(',')[1] || ''
        const domain = searchParams.get('domain')
        const isJoorProduction = searchParams.get('production') ? searchParams.get('production').toLowerCase() === 'true' : false
        if (accountId && code && retailerId && domain) {
          await this.initJoorService({ accountId, code, retailerId, domain, isJoorProduction })
        } else {
          console.warn('Joor service isn\'t active.')
        }
      } catch (error) {
        console.log('setUpJoorService', error)
      }
    },

    navigateNewTab (buildingId) {
      window.open(`${location.origin}/${buildingId}`, '_blank')
    },

    checkIsNeedNavigate (marker) {
      if (Object.keys(this.property).length && this.property.buildings.length > 0) {
        const buildingIds = this.property.buildings.filter(building => !building.unavailable).map(building => building.objectId)
        return !buildingIds.includes(marker.buildingId)
      }
      return true
    },

    directToTarget (marker) {
      setTimeout(() => {
        if (this.isHaveStartUp) {
          setTimeout(() => { // wait for planetView animation end
            this.gotoTarget(marker)
            sessionStorage.removeItem('marker')
          }, 1000)
        } else {
          this.directToTarget(marker)
        }
      }, 500)
    }
  }
}
</script>

<style lang="stylus" rel="stylesheet/stylus" scoped>
@import '~css/addon/main'
@import '~css/common/extends'

.enlarge-floorplan-outer-wrapper{
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.i-main {
  @extend .absolute-full
}
.main-float-btns{
  position: absolute;
  top: 24px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.expand-btn{
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto
  background-color: rgba(0, 0, 0,.3);
  padding: 8px
  border-radius: 50%
  color: #FFF;
  font-size: 20px
  cursor: pointer
}
.home-btn{
  pointer-events: auto;
  background-color: rgba(0, 0, 0,.3);
  padding: 8px
  border-radius: 50%;
  color: #FFF;
  font-size: 20px;
  cursor: pointer;
  display: flex;
}
.home-btn:hover{
  background-color: rgba(0, 0, 0, 0.4)
}
.expand-btn:hover{
  background-color: rgba(0, 0, 0, 0.4)
}

.main-krpano-wrapper {
  z-index: $main-krpano-wrapper-z
}

.main-footer {
  @extend .flex-center-column

  position: fixed
  width: 100%
  left: 0
  right: 0
  bottom: 0
  font-size: 0.9375rem
  transform: translateY(100%)
  z-index: $viewer-list-footer-z
  pointer-events: none

  @media (min-width: $response) {
    font-size: 0.875rem
  }

  .main-viewer-list {
    transition: opacity .25s ease-out
  }

  .main-viewer-list-collapse {
    width: 5.125rem
    height: 2.675rem
    margin: 0
    padding: 0
    position: relative
    display: flex
    align-items: center
    justify-content: center
    border: 0
    background: none
    font-size: inherit
    color: $white
    text-shadow: 0 1px 1px #000000

    i {
      font-size: 1.2em
    }

    label {
      display: none
    }

    &::after {
      position: absolute
      top: 0
      left: 0
      right: 0
      bottom: 0
      z-index: -1
      content: ''
      border-radius: 1.75em 1.75em 0 0
      background-color: rgba(0,0,0,0.8)
      transform: perspective(5px) rotateX(2deg) scale(1.5)
      transform-origin: bottom
    }
  }

  &.main-footer-active {
    transform: translateY(0)

    .main-viewer-list-collapse {
      &::after {
        background-color: rgba(0,0,0,0.2)
      }

      @media (max-width: $response) {
        width: 100%
        height: 3em
        padding: 0 14px
        justify-content: flex-start

        i {
          margin-left: .5em
        }

        label {
          display: inline
        }

        &::after {
          border-radius: 0
        }
      }
    }
  }

  .main-footer-tool {
    height: fill-available
    position: absolute
    left: 0
    right: 0
    top: 0
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    transform: translateY(-100%)

    & > div {
      display: flex

      & > * {
        pointer-events: auto
      }
    }

    & > .main-footer-tool-1 {
      justify-content: center
    }
  }
}

.floorplan-wrapper {
  @extend .absolute-full-width
  @extend .flex-center
  bottom: 0
  z-index: $mobile-floorplan-wrapper-z
  opacity: 0
  transform: translateY(100%)
  transition: transform .25s ease-out, opacity .25s ease-out
}

.floorplan-wrapper-active {
  opacity: 1
  transform: none
}

.floorplan-wrapper-collapse {
  display: none
}

.enlarge-floorplan-wrapper {
  position: absolute;
  left: 50vw;
  top: 50vh;
  transform: translate(-50%, -50%);
  height: 0;
  opacity: 0;
  overflow: hidden;
  animation: fadeOut 0.3s;

  &.enlarge-floorplan-wrapper-active {
    height: auto;
    opacity: 1;
    animation: fadeIn 0.3s;
  }
}

.viewer-layer-inner {
  .i-favorite {
    position: absolute
    top: 13px
    left: 18px
    z-index: 1
  }
}

.icon {
  &.icon-collapse-left {
    $w = $i-main-arrow-collapse-short-side
    $h = 25px
    bg-size($w, $h)
    background-image: url('../../components/Icon/img/collapse-arrow/collapse-left.svg')
  }

  &.icon-collapse-right {
    $w = $i-main-arrow-collapse-short-side
    $h = 25px
    bg-size($w, $h)
    background-image: url('../../components/Icon/img/collapse-arrow/collapse-right.svg')
  }

  &.icon-collapse-up {
    $w = 25px
    $h = $i-main-arrow-collapse-short-side
    bg-size($w, $h)
    background-image: url('../../components/Icon/img/collapse-arrow/collapse-up.svg')
  }

  &.icon-collapse-down {
    $w = 25px
    $h = $i-main-arrow-collapse-short-side
    bg-size($w, $h)
    background-image: url('../../components/Icon/img/collapse-arrow/collapse-down.svg')
  }
}

.share-container {
  @extend .flex-center-column
  position: relative
  width: 240px
  margin: 18px auto 0

  .btn-close {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #343a40;
    color: white;
    border-radius: 5px;
    padding: 6px 28px;
    margin-top: 18px;
  }
}

.floorplan-self {
  padding: 10px
}

.popup-wrapper {
  iframe {
    position: fixed
    top: 50%
    left: 50%
    transform: translate(-50%,-50%)
    width: 100%
    height: 100%
  }
}

.marker-info-desktop {
  display: none
}

.marker-info-mobile {
  display: block
}

@keyframes fadeIn {
  from {
    opacity: 0;
    height: auto;
  }
  to {
    opacity: 1;
    height: auto;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
    height: auto;
  }
  99% {
    opacity: 0;
    height: auto;
  }
  100% {
    opacity: 0;
    height: 0;
  }
}

@media (orientation: landscape) {
  .floorplan-wrapper {
    position: absolute
    height: 100%
    top: 0
    bottom: 0
    width: auto
    left: $i-main-floorplan-wrapper-landscape-pos
    right: auto
    transform: translateX(-($i-main-floorplan-wrapper-landscape-transform-x))
  }

  .floorplan-wrapper-active {
    transform: none
  }
}

@media (min-width: $response) {

  .floorplan-wrapper {
    width: auto
    height: auto
    top: auto
    left: $i-main-floorplan-wrapper-landscape-pos
    right: auto
    bottom: 160px + 15px
    z-index: $floorplan-wrapper-z
    opacity: 1
    transform: translateX(-($i-main-floorplan-wrapper-landscape-transform-x)) translateX($i-main-arrow-collapse-short-side + $i-main-arrow-collapse-pd * 2)
  }

  .floorplan-wrapper-has-buildings {
    bottom: 200px + 15px
  }

  .floorplan-wrapper-collapse {
    position: relative
    display: flex
    padding: $i-main-arrow-collapse-pd
    cursor: pointer
  }

  .floorplan-wrapper-active {
    transform: none
  }

  .share-container {
    display: flex
    flex-direction: row
    width: auto
    margin-top: 41px

    .share-list {
      margin-left: $i-main-share-container-share-list-margin
    }

    .btn-close {
      display: none;
    }
  }

  .floorplan-self {
    padding: 20px
  }

  .marker-info-desktop {
    display: block
  }

  .marker-info-mobile {
    display: none !important
  }

  .home-btn{
    display: none;
  }
  .expand-btn{
    display: none;
  }

}
</style>

<style lang="stylus" rel="stylesheet/stylus">
@import '~css/addon/main'

.theme-rtl.theme-rtl-overlap {
  .icon.icon-collapse-left,
  .icon.icon-collapse-right {
    transform: rotateY(180deg)
  }

  @media (orientation: landscape) {
    .floorplan-wrapper {
      right: $i-main-floorplan-wrapper-landscape-pos
      left: auto
      transform: translateX($i-main-floorplan-wrapper-landscape-transform-x)
    }

    .floorplan-wrapper-active {
      transform: none
    }
  }

  @media (min-width: $response) {
    .floorplan-wrapper {
      right: $i-main-floorplan-wrapper-landscape-pos
      left: auto
      transform: translateX($i-main-floorplan-wrapper-landscape-transform-x) translateX(-($i-main-arrow-collapse-short-side + $i-main-arrow-collapse-pd * 2))
    }

    .floorplan-wrapper-active {
      transform: none
    }

    .share-container {
      .share-list {
        margin-left: 0
        margin-right: $i-main-share-container-share-list-margin
      }
    }
  }
}

.i-main-music-player {
  position: absolute;
  top: 1.6875rem;
  left: 7.5rem;

  @media (max-width: $response - 1) {
    top: 6.5rem;
    left: 50%;
    transform: translateX(-50%);
  }
}

.result-block {
  display: flex
  justify-content: center
  flex-wrap: wrap
}

.viewer-layer-subtitle {
  margin: 0 20px 20px;
  font-size: 20px;
}

.card {
  width: 100%
  max-width: 400px
  cursor: pointer
  position: relative
  background: gray
  display: flex
  flex-direction: column
  margin: 0 15px 30px
  box-shadow: 0px 3px 8px rgba($black, 0.5)

  .card-image {
    height: 200px
    background-size: contain
    background-repeat: no-repeat
    background-position: center
  }
  .card-image-photo {
    height: 200px
    background-size: cover
    background-repeat: no-repeat
    background-position: center
  }
  .card-content {
    overflow: auto
    position: relative
    height: 180px
    background: $white
    color: $black
    padding: 14px 20px

    .card-title {
      width: 100%
      height: 20px
      margin-bottom: 10px
      font-size: 1rem // 16
      line-height: 20px
      color: $black
      text-shadow: none
      overflow : hidden
      text-overflow : ellipsis
      white-space: no-wrap
    }
    .card-category {
      margin-bottom: 10px
    }
    .card-desc {
      text-shadow: none
      color: $black
      font-size: 12px
      line-height: 16px
      // padding: 12px
      height: 65px
      // width: 100%
      overflow : hidden
      position: relative
      text-overflow : ellipsis
      overflow-wrap: break-word
      display: -webkit-box
      -webkit-box-orient: vertical
      -webkit-line-clamp: 4
    }
  }
  .card-more {
    position: absolute
    bottom: 5px
    right: 5px
    width:80px
    height:22px
    background: #000
    color: #fff
    text-align: center
    line-height: 22px
    border-radius 8px
  }
  .card-navigate {
    position: absolute
    bottom: 5px
    right: 5px
    padding: 4px 8px
    background: $pink-color
    color: $white
    text-align: center
    line-height: 22px
    border-radius 8px
  }
}

.search-wrap {
  display flex
  flex-direction column
}
.search-block {
  width 800px
  max-width 90%
  margin 0 auto
  flex-basis 100px
  align-items center
  display flex

  .category-search {
    flex 1
    position relative

    .i-select .i-select-input-wrapper {
      border-radius: 10px 0 0 10px;
    }
    &:after {
      content ''
      display block
      position absolute
      top 0
      right 0
      width 2px
      height 60%
      background #C4C4C4
      margin auto
      bottom 0
    }
  }
  .marker-search {
    flex 3

    .i-select .i-select-input-wrapper {
      border-radius: 0 10px 10px 0;
    }
  }
}
.search-result {
  flex 1
}
.search-result-cards {
  width: 100%
  margin: 0 auto 30px
  height: auto !important
}
.show-more {
  text-align: center

  button {
    padding: 10px
    color: #fff
    border: 2px solid #fff
    border-radius: 4px
    background-color: transparent
  }
}
.search-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.h-100 {
  height: 100%
}
.overflow-auto {
  overflow: auto
}
</style>
